import {useContext, useState} from 'react';
import { Send } from "./InputEditor/Send/Send.tsx";
import {useAppStore} from "../../GlobalStore/GlobalStore.ts";
import Stop from "./InputEditor/Stop/Stop.tsx";
import InputEditor from "./InputEditor/InputEditor.tsx";
import { ImSpinner2 } from "react-icons/im";
import MediaDisplay from "./MediaDisplay/MediaDisplay.tsx";
import MediaInput from "./MediaInput/MediaInput.tsx";
import {isMobileApp, isChrome} from "../../Utils/misc/envNameUtils.ts";
import {getBlurStyles} from "../../Utils/misc/cssStyleUtils.ts";
import {GlobalContext} from "../../App.tsx";
import ModeSelector from './ModeSelector/ModeSelector.tsx';
import ModalitySelector from './ModalitySelector/ModalitySelector.tsx';
import VoiceMode from './VoiceMode/VoiceMode.tsx';
import MessageQueueManager from "../MessageQueue/MessageQueueManager.tsx";
import {useMessageQueue} from "../../Utils/messageQueue/useMessageQueue.ts";

interface BottomProps {}

const Bottom = ({}: BottomProps) => {
    const { editorRef } = useContext(GlobalContext);
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { canSubmit, isStopping, hasAttachment, hasText } = dynamicStateObj;
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment } = environmentObj;
    const userObj = useAppStore((state: any) => state.userObj);
    const { subscriptionPlan, monthlyFreeMessages } = userObj;
    const { getQueueSize } = useMessageQueue();
    const [isQueueManagerOpen, setIsQueueManagerOpen] = useState(false);

    const queueSize = getQueueSize();

    const handleSendClick = () => {
        if ((hasAttachment || hasText) && editorRef.current) {
            if (isMobileApp(environment)) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    functionName: "triggerHapticFeedback",
                    data: [{ _: '_' }]
                }));
            }
            // Simulate Enter keydown on the textarea
            const textarea = document.querySelector('.inputEditor') as HTMLTextAreaElement;
            if (textarea) {
                const event = new KeyboardEvent('keydown', {
                    key: 'Enter',
                    shiftKey: environment.includes('mobile'),
                    bubbles: true,
                    cancelable: true
                });
                textarea.dispatchEvent(event);
            }
        }
    };

    const getInputContainerStyles = () => {
        const styles = [];

        if (environment.includes("jetbrains") || environment === "vscode") styles.push("mb-0.5");
        else if (isMobileApp(environment)) styles.push("mb-1");
        else if (environment === "chrome") styles.push("mb-0");
        else styles.push("mb-3");

        return styles.join(' ');
    };

    const inputContainerStyles = getInputContainerStyles();
    const blurStyles = getBlurStyles([dynamicStateObj.isMenuOpen, dynamicStateObj.visiblePopup?.includes("settings")]);
    const editorBlurOpacity = isMobileApp(environment) || environment === "mobile_web" ? 0 : 50;
    const editorBlurStyles = getBlurStyles([dynamicStateObj.isUploadOpen], editorBlurOpacity);

    return (
        <div className={`flex flex-col ${blurStyles}`}>
            <MediaDisplay />
            <div className="flex mx-auto w-full px-3 justify-center">
                <div className={`relative max-w-[700px] w-full min-w-64 box-border ${inputContainerStyles} mt-1.5`}>
                    <div className={`flex flex-col ${editorBlurStyles} rounded-3xl border border-neutral-400 dark:border-neutral-600 overflow-hidden`}>
                        <div className="flex items-center w-full">
                            <div
                                className={`flex-grow pl-4 pr-2 py-2 cursor-text ${environment.includes("mobile") ? "min-h-[2.90rem]" : ""}`}
                                onClick={() => editorRef.current?.focus()}
                            >
                                <InputEditor />
                            </div>
                        </div>
                        <div className="flex items-center justify-between pl-3 pr-3 pb-2 pt-1">
                            <div className="flex items-center space-x-2">
                                <MediaInput />
                                {(subscriptionPlan !== null || monthlyFreeMessages > 0) && (
                                    <>
                                        <ModeSelector />
                                        {environment === 'chrome' && <ModalitySelector />}
                                    </>
                                )}
                            </div>
                            <div className={`flex items-center justify-center ${isStopping ? "space-x-3 mr-1" : "space-x-2"}`}>
                                {/* Queue Button */}
                                {queueSize > 0 && (
                                    <button
                                        onClick={() => setIsQueueManagerOpen(true)}
                                        className="w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 border hover:bg-neutral-200 dark:hover:bg-neutral-700 border-neutral-300 dark:border-neutral-600 bg-blue-500 text-white"
                                        title={`${queueSize} message${queueSize > 1 ? 's' : ''} queued`}
                                    >
                                        <span className="text-sm font-medium">
                                            {queueSize}
                                        </span>
                                    </button>
                                )}

                                {!isMobileApp(environment) && !isChrome(environment) && <VoiceMode />}
                                {isStopping ? (
                                    <ImSpinner2 className="animate-spin text-gray-500 mr-1" size={24} />
                                ) : canSubmit ? (
                                    <Send onClick={handleSendClick} />
                                ) : (
                                    <Stop />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Message Queue Manager */}
            <MessageQueueManager
                isOpen={isQueueManagerOpen}
                onClose={() => setIsQueueManagerOpen(false)}
            />
        </div>
    );
};

export default Bottom;
